import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Sparkles, Building2, Users, CheckSquare, Square,
  AlertTriangle, Save, Truck, Clock, PenLine, X, ChevronRight, ChevronLeft, RotateCcw,
} from "lucide-react";
import SignatureCanvas from "@/components/SignatureCanvas";

// =============================================================================
// AutoGeneratorBatch — Phase 2-A
// Generates time-entries + driving-logs for MANY drivers in one call.
// Two modes: explicit driver list · whole department.
// =============================================================================
const MONTHS = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

const MODES = [
  { key: "drivers",    label: "Mehrere Fahrer",       icon: Users },
  { key: "department", label: "Ganze Abteilung",      icon: Building2 },
];

export default function AutoGeneratorBatch() {
  const today = new Date();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ruleGroups, setRuleGroups] = useState([]);

  const [form, setForm] = useState({
    mode: "drivers",
    department_id: "",
    driver_filter_department_id: "",  // filters which drivers show in picker (mode=drivers)
    driver_filter_query: "",           // search filter for driver picker
    employee_ids: [],
    rule_group_id: "",
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    overtime_target_hours_min: 0,
    overtime_target_hours_max: 0,
    include_saturdays: false,
    include_inactive: false,
    vehicle_overrides: {},   // emp_id → veh_id (leave empty → auto from last entry)
  });

  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);          // {batch_run_id, results: [...]}
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const [committing, setCommitting] = useState(false);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [sigStep, setSigStep] = useState(0);
  const [sigOverrides, setSigOverrides] = useState({});  // emp_id → data_url

  useEffect(() => {
    (async () => {
      try {
        const [emp, dep, veh, rg] = await Promise.all([
          api.get("/employees"),
          api.get("/departments"),
          api.get("/vehicles"),
          api.get("/auto-gen/rule-groups"),
        ]);
        setEmployees((emp.data || []).filter(e => e.role === "fahrer"));
        setDepartments(dep.data || []);
        setVehicles((veh.data || []).filter(v => v.status === "aktiv"));
        setRuleGroups(rg.data?.groups || []);
      } catch {
        toast.error("Daten konnten nicht geladen werden");
      }
    })();
  }, []);

  function f(k, v) {
    setForm(p => ({ ...p, [k]: v }));
  }

  const activeEmployees = useMemo(() => {
    let list = [...employees];
    if (!form.include_inactive) {
      list = list.filter(e => e.status !== "abgemeldet" && e.status !== "inaktiv");
    }
    return list.sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""));
  }, [employees, form.include_inactive]);

  const driversByDept = useMemo(() => {
    if (!form.department_id) return activeEmployees;
    return activeEmployees.filter(e => e.department_id === form.department_id);
  }, [activeEmployees, form.department_id]);

  const pickerEmployees = useMemo(() => {
    let list = activeEmployees;
    if (form.driver_filter_department_id) {
      list = list.filter(e => e.department_id === form.driver_filter_department_id);
    }
    const q = (form.driver_filter_query || "").trim().toLowerCase();
    if (q) {
      list = list.filter(e =>
        (e.first_name || "").toLowerCase().includes(q) ||
        (e.last_name || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.personnel_number || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeEmployees, form.driver_filter_department_id, form.driver_filter_query]);

  const deptCounts = useMemo(() => {
    const m = {};
    for (const e of activeEmployees) m[e.department_id || ""] = (m[e.department_id || ""] || 0) + 1;
    return m;
  }, [activeEmployees]);

  const targetCount = useMemo(() => {
    if (form.mode === "drivers")    return form.employee_ids.length;
    if (form.mode === "department") return driversByDept.length;
    return 0;
  }, [form.mode, form.employee_ids, driversByDept]);

  function toggleEmp(id) {
    const cur = new Set(form.employee_ids);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    f("employee_ids", [...cur]);
  }

  function selectAll() { f("employee_ids", pickerEmployees.map(e => e.id)); }
  function clearAll() { f("employee_ids", []); }

  async function preview() {
    if (!form.rule_group_id) {
      toast.error("Regel-Gruppe ist Pflicht");
      return;
    }
    if (form.mode === "drivers" && form.employee_ids.length === 0) {
      toast.error("Mindestens einen Fahrer auswählen");
      return;
    }
    if (form.mode === "department" && !form.department_id) {
      toast.error("Abteilung auswählen");
      return;
    }
    if (targetCount > 200) {
      toast.error(`Zu viele Fahrer (${targetCount}). Maximum 200 pro Lauf.`);
      return;
    }
    setBusy(true);
    setBatch(null);
    try {
      const payload = {
        mode: form.mode,
        employee_ids: form.mode === "drivers" ? form.employee_ids : undefined,
        department_id: form.mode === "department" ? form.department_id : undefined,
        rule_group_id: form.rule_group_id,
        year: form.year,
        month: form.month,
        overtime_target_hours_min: parseFloat(form.overtime_target_hours_min) || 0,
        overtime_target_hours_max: parseFloat(form.overtime_target_hours_max) || 0,
        include_saturdays: form.include_saturdays,
        include_inactive: form.include_inactive,
        vehicle_overrides: form.vehicle_overrides,
      };
      const { data } = await api.post("/auto-gen/batch-preview", payload);
      setBatch(data);
      // Preselect every successful run (no errors)
      setSelectedRuns(new Set(
        (data.results || []).filter(r => r.errors === 0).map(r => r.run_id),
      ));
      toast.success(`${data.drivers_count} Fahrer vorbereitet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally { setBusy(false); }
  }

  async function commit() {
    if (selectedRuns.size === 0) {
      toast.error("Keine Fahrer ausgewählt");
      return;
    }
    // Open per-driver signature collection modal first
    setSigStep(0);
    setSigOverrides({});
    setSigModalOpen(true);
  }

  async function commitConfirmed() {
    setCommitting(true);
    try {
      const { data } = await api.post("/auto-gen/batch-commit", {
        run_ids: [...selectedRuns],
        signature_overrides: sigOverrides,
      });
      toast.success(`Commit erfolgreich: ${data.totals.created} neu · ${data.totals.updated} überschrieben · ${data.totals.drv_created} Lenkprotokolle`);
      setBatch(null);
      setSelectedRuns(new Set());
      setSigModalOpen(false);
      setSigOverrides({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Commit fehlgeschlagen");
    } finally { setCommitting(false); }
  }

  function toggleRunSelection(run_id) {
    const next = new Set(selectedRuns);
    if (next.has(run_id)) next.delete(run_id); else next.add(run_id);
    setSelectedRuns(next);
  }

  function selectAllRuns(only_ok = true) {
    if (!batch?.results) return;
    const all = batch.results
      .filter(r => !only_ok || r.errors === 0)
      .map(r => r.run_id);
    setSelectedRuns(new Set(all));
  }

  const yearOpts = Array.from({ length: 5 }, (_, i) => today.getFullYear() - 1 + i);

  return (
    <div className="space-y-6" data-testid="autogen-batch-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2">
            <Sparkles className="w-3 h-3" /> Phase 2-A · Massen-Generator
          </div>
          <h1 className="font-display text-3xl font-bold text-zinc-50">Auto-Generator — Massen-Lauf</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Generiere Zeiterfassung + Lenkprotokoll für mehrere Fahrer oder eine
            ganze Abteilung in einem Schwung. Fahrzeug und Start-KM werden pro
            Fahrer automatisch aus dem letzten Eintrag übernommen.
          </p>
        </div>
      </header>

      {/* Mode selector */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="autogen-batch-mode">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2.5 font-semibold">Modus</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MODES.map(m => {
            const I = m.icon;
            const active = form.mode === m.key;
            return (
              <button key={m.key}
                data-testid={`autogen-batch-mode-${m.key}`}
                onClick={() => f("mode", m.key)}
                className={`p-3 rounded border-2 text-left transition-all ${active
                  ? "bg-purple-500/10 border-purple-500/40 text-purple-200"
                  : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"}`}>
                <I className={`w-5 h-5 mb-1 ${active ? "text-purple-400" : "text-zinc-500"}`} />
                <div className="font-semibold text-sm">{m.label}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Common config */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="autogen-batch-config">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Monat" icon={Calendar}>
            <div className="grid grid-cols-2 gap-2">
              <select className={INP} value={form.month} data-testid="autogen-batch-month"
                onChange={e => f("month", parseInt(e.target.value, 10))}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <select className={INP} value={form.year} data-testid="autogen-batch-year"
                onChange={e => f("year", parseInt(e.target.value, 10))}>
                {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </Field>

          <Field label="Regel-Gruppe" icon={Sparkles}>
            <select className={INP} value={form.rule_group_id} data-testid="autogen-batch-rule"
              onChange={e => f("rule_group_id", e.target.value)}>
              <option value="">— wählen —</option>
              {ruleGroups.map(r => (
                <option key={r.id} value={r.id}>{r.code} — {r.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Überstunden-Ziel pro Fahrer (Stunden, Zufalls-Range)" icon={Clock}>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" min="0" max="80" step="0.25" className={INP}
                placeholder="von (z.B. 0)"
                data-testid="autogen-batch-overtime-min"
                value={form.overtime_target_hours_min}
                onChange={e => f("overtime_target_hours_min", e.target.value)}/>
              <input type="number" min="0" max="80" step="0.25" className={INP}
                placeholder="bis (z.B. 8)"
                data-testid="autogen-batch-overtime-max"
                value={form.overtime_target_hours_max}
                onChange={e => f("overtime_target_hours_max", e.target.value)}/>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Jeder Fahrer bekommt einen zufälligen Wert in 0,25h-Schritten.</div>
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2 text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.include_saturdays}
              onChange={e => f("include_saturdays", e.target.checked)}
              data-testid="autogen-batch-saturdays"
              className="w-4 h-4 rounded accent-purple-500"/>
            Samstage einbeziehen
          </label>
          <label className="flex items-center gap-2 text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.include_inactive}
              onChange={e => f("include_inactive", e.target.checked)}
              data-testid="autogen-batch-inactive"
              className="w-4 h-4 rounded accent-zinc-500"/>
            Auch abgemeldete Fahrer einbeziehen
          </label>
          <div className="flex items-center gap-2 text-zinc-400 text-[11px] px-2 py-1 rounded bg-amber-500/5 border border-amber-500/20" data-testid="autogen-batch-overwrite-info">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400"/>
            Bestehende Einträge werden automatisch überschrieben
          </div>
        </div>
      </section>

      {/* Mode-specific selectors */}
      {form.mode === "department" && (
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5" data-testid="autogen-batch-dept-section">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 font-semibold">
            Abteilung
          </div>
          <select className={INP} value={form.department_id} data-testid="autogen-batch-dept-select"
            onChange={e => f("department_id", e.target.value)}>
            <option value="">— wählen —</option>
            {departments.map(d => {
              const cnt = activeEmployees.filter(e => e.department_id === d.id).length;
              return <option key={d.id} value={d.id}>{d.name} ({cnt} Fahrer)</option>;
            })}
          </select>
        </section>
      )}

      {form.mode === "drivers" && (
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5" data-testid="autogen-batch-drivers-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
              Fahrer auswählen — {form.employee_ids.length} / {pickerEmployees.length} sichtbar · {activeEmployees.length} insgesamt
            </div>
            <div className="flex gap-2">
              <button onClick={selectAll} data-testid="autogen-batch-select-all"
                className="text-[11px] text-blue-400 hover:text-blue-300">Alle sichtbaren</button>
              <button onClick={clearAll} data-testid="autogen-batch-clear-all"
                className="text-[11px] text-zinc-500 hover:text-zinc-300">Keine</button>
            </div>
          </div>

          {/* Filter row — Abteilung + Suche */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <select className={INP} value={form.driver_filter_department_id}
              data-testid="autogen-batch-driver-dept-filter"
              onChange={e => f("driver_filter_department_id", e.target.value)}>
              <option value="">Alle Abteilungen ({activeEmployees.length} Fahrer)</option>
              {departments.map(d => {
                const cnt = deptCounts[d.id] || 0;
                if (cnt === 0) return null;
                return <option key={d.id} value={d.id}>{d.name} ({cnt} Fahrer)</option>;
              })}
            </select>
            <input type="text" className={INP}
              placeholder="Fahrer suchen (Name, E-Mail, Personalnr.)…"
              value={form.driver_filter_query}
              data-testid="autogen-batch-driver-search"
              onChange={e => f("driver_filter_query", e.target.value)}/>
          </div>

          <div className="max-h-72 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
            {pickerEmployees.length === 0 && (
              <div className="col-span-full text-center text-xs text-zinc-500 py-6">
                Keine Fahrer in dieser Auswahl.
              </div>
            )}
            {pickerEmployees.map(e => {
              const checked = form.employee_ids.includes(e.id);
              return (
                <label key={e.id} data-testid={`autogen-batch-emp-${e.id}`}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer ${checked ? "bg-purple-500/10 text-purple-200" : "hover:bg-zinc-800/50 text-zinc-300"}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleEmp(e.id)}
                    className="w-3.5 h-3.5 rounded accent-purple-500"/>
                  <span className="truncate">{e.last_name}, {e.first_name}</span>
                  {(e.status === "abgemeldet" || e.status === "inaktiv") && (
                    <span className="text-[9px] text-zinc-500">[{e.status}]</span>
                  )}
                </label>
              );
            })}
          </div>
        </section>
      )}

      {/* Generate button */}
      <div className="flex justify-end items-center gap-3">
        <div className="text-sm text-zinc-400">
          <span className="font-semibold text-zinc-200">{targetCount}</span>{" "}
          {targetCount === 1 ? "Fahrer" : "Fahrer"} im Lauf
        </div>
        <button onClick={preview} disabled={busy || targetCount === 0}
          data-testid="autogen-batch-preview-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium disabled:opacity-40">
          <Sparkles className="w-4 h-4"/>
          {busy ? "Generiere alle Fahrer …" : "Massen-Vorschau generieren"}
        </button>
      </div>

      {/* Batch results */}
      {batch && <BatchResults batch={batch} selectedRuns={selectedRuns}
        onToggle={toggleRunSelection} onSelectAll={selectAllRuns}
        onCommit={commit} committing={committing} />}

      {/* Per-driver signature wizard — admin signs once per selected driver
          (Skippable). Same signature applied to every day for that driver. */}
      {sigModalOpen && batch && (() => {
        const selectedResults = batch.results.filter(r => selectedRuns.has(r.run_id));
        const total = selectedResults.length;
        if (total === 0) {
          setSigModalOpen(false);
          return null;
        }
        const cur = selectedResults[Math.min(sigStep, total - 1)];
        const curSig = sigOverrides[cur.employee_id] || "";
        const isLast = sigStep >= total - 1;

        function setCurSig(v) {
          setSigOverrides(prev => ({ ...prev, [cur.employee_id]: v }));
        }
        function skip() { isLast ? commitConfirmed() : setSigStep(s => s + 1); }

        return (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="autogen-batch-sig-modal">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-xl">
              <header className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">
                    Unterschrift {sigStep + 1} von {total}
                  </div>
                  <h3 className="font-display text-zinc-50 font-semibold mt-0.5">
                    {cur.employee_name}
                  </h3>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {cur.summary?.days_generated || 0} Tage werden mit dieser Unterschrift signiert
                  </div>
                </div>
                <button onClick={() => setSigModalOpen(false)} disabled={committing}
                  className="text-zinc-500 hover:text-zinc-200">
                  <X className="w-4 h-4" />
                </button>
              </header>
              <div className="p-5 space-y-3">
                <SignatureCanvas
                  value={curSig}
                  onChange={setCurSig}
                  label={`Unterschrift ${cur.employee_name}`}
                  testid={`autogen-batch-sig-${cur.employee_id}`}
                />
                <div className="flex items-center justify-between text-[11px] text-zinc-500">
                  <span>Falls leer → Standard-Unterschrift aus Mitarbeiter-Karte (falls vorhanden) wird genutzt.</span>
                  <div className="flex items-center gap-2">
                    {selectedResults.map((r, i) => (
                      <div key={r.run_id}
                        className={`w-1.5 h-1.5 rounded-full ${
                          i === sigStep ? "bg-amber-400" :
                          sigOverrides[r.employee_id] ? "bg-emerald-500" : "bg-zinc-700"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <footer className="px-5 py-3 border-t border-zinc-800 flex justify-between items-center">
                <button onClick={() => setSigStep(s => Math.max(0, s - 1))}
                  disabled={sigStep === 0 || committing}
                  className="text-xs text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1 disabled:opacity-30">
                  <ChevronLeft className="w-3 h-3" /> Zurück
                </button>
                <div className="flex gap-2">
                  <button onClick={skip} disabled={committing}
                    data-testid="autogen-batch-sig-skip"
                    className="px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200">
                    {isLast ? "Ohne Signatur speichern" : "Überspringen"}
                  </button>
                  {!isLast && (
                    <button onClick={() => setSigStep(s => s + 1)} disabled={committing}
                      data-testid="autogen-batch-sig-next"
                      className="px-4 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded inline-flex items-center gap-1">
                      Nächster <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                  {isLast && (
                    <button onClick={commitConfirmed} disabled={committing}
                      data-testid="autogen-batch-sig-finish"
                      className="px-4 py-2 text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-30 text-white rounded font-medium">
                      {committing ? "Speichere …" : `Alle ${total} Fahrer speichern`}
                    </button>
                  )}
                </div>
              </footer>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ============================================================================
// BatchResults — table of per-driver outcomes with opt-out per driver
// ============================================================================
function BatchResults({ batch, selectedRuns, onToggle, onSelectAll, onCommit, committing }) {
  const okCount = batch.results.filter(r => r.errors === 0).length;
  const errCount = batch.results.filter(r => r.errors > 0).length;
  const noSig = batch.results.filter(r => !r.has_signature).length;

  return (
    <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid="autogen-batch-results">
      <header className="px-5 py-4 border-b border-zinc-800 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Massen-Vorschau</div>
          <h2 className="font-display font-semibold text-zinc-50">
            {batch.rule_code} · {batch.year}/{String(batch.month).padStart(2,"0")} · {batch.drivers_count} Fahrer
          </h2>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <Stat label="OK" value={okCount} tone="emerald" />
          {errCount > 0 && <Stat label="Mit Fehlern" value={errCount} tone="red" />}
          {noSig > 0 && <Stat label="Ohne Signatur" value={noSig} tone="amber" />}
          <Stat label="Ausgewählt" value={selectedRuns.size} tone="blue" />
        </div>
      </header>

      <div className="px-5 py-2 border-b border-zinc-800 flex flex-wrap gap-3 text-xs">
        <button onClick={() => onSelectAll(true)}
          className="text-blue-400 hover:text-blue-300">Alle ohne Fehler auswählen</button>
        <button onClick={() => onSelectAll(false)}
          className="text-zinc-400 hover:text-zinc-200">Wirklich alle auswählen</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Fahrer</th>
              <th className="text-left px-3 py-2">Fahrzeug</th>
              <th className="text-right px-3 py-2">Tage gen.</th>
              <th className="text-right px-3 py-2">Skipt</th>
              <th className="text-right px-3 py-2">Überschr.</th>
              <th className="text-right px-3 py-2">Überstd.</th>
              <th className="text-left px-3 py-2">Sig.</th>
              <th className="text-right px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {batch.results.map(r => {
              const sel = selectedRuns.has(r.run_id);
              const sum = r.summary || {};
              const ot = sum.overtime || {};
              const ok = r.errors === 0;
              return (
                <tr key={r.run_id} data-testid={`autogen-batch-row-${r.employee_id}`}
                  className="border-b border-zinc-800/40 hover:bg-zinc-800/30">
                  <td className="px-3 py-2 cursor-pointer" onClick={() => onToggle(r.run_id)}>
                    {sel ? <CheckSquare className="w-4 h-4 text-purple-400" /> : <Square className="w-4 h-4 text-zinc-600" />}
                  </td>
                  <td className="px-3 py-2 text-zinc-100">{r.employee_name}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.vehicle_plate ? (
                      <span className="inline-flex items-center gap-1 text-zinc-300">
                        <Truck className="w-3 h-3 text-zinc-500" />
                        <span className="font-mono">{r.vehicle_plate}</span>
                        {r.vehicle_auto && (
                          <span className="text-[9px] text-blue-300 bg-blue-500/10 border border-blue-500/20 px-1 rounded">auto</span>
                        )}
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{sum.days_generated || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{sum.days_skipped || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-300">{sum.days_with_existing_entry || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-300">
                    {r.overtime_hours ? `${r.overtime_hours}h` : (ot.distributed_minutes ? `${Math.round(ot.distributed_minutes/60*10)/10}h` : "—")}
                  </td>
                  <td className="px-3 py-2">
                    {r.has_signature
                      ? <PenLine className="w-3.5 h-3.5 text-emerald-400" title="Signatur hinterlegt" />
                      : <PenLine className="w-3.5 h-3.5 text-amber-400" title="Keine Signatur" />}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {ok
                      ? <span className="text-[10px] uppercase text-emerald-400 font-semibold">OK</span>
                      : <span className="text-[10px] uppercase text-red-400 font-semibold">{r.errors} Fehler</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="px-5 py-4 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-zinc-500">
          Batch-ID: <span className="font-mono">{batch.batch_run_id.slice(0,8)}…</span>
        </p>
        <button onClick={onCommit} disabled={committing || selectedRuns.size === 0}
          data-testid="autogen-batch-commit-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium disabled:opacity-40">
          <Save className="w-4 h-4"/>
          {committing ? "Speichere alle …" : `${selectedRuns.size} Fahrer × Monat speichern`}
        </button>
      </footer>
    </section>
  );
}

// ============================================================================
// Tiny helpers
// ============================================================================
const INP = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-purple-500/60 outline-none";

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3"/>} {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = {
    emerald: "text-emerald-300", amber: "text-amber-300",
    blue: "text-blue-300", red: "text-red-300", zinc: "text-zinc-400",
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-bold tabular-nums ${map[tone]}`}>{value}</div>
    </div>
  );
}
